<template>
  <div class="user-management">
    <div class="page-header">
      <h2>用户管理</h2>
      <el-button type="primary" @click="showCreateDialog">新增用户</el-button>
    </div>

    <!-- 搜索表单 -->
    <el-form :inline="true" :model="searchForm" class="search-form">
      <el-form-item label="用户名">
        <el-input v-model="searchForm.username" placeholder="请输入用户名" clearable />
      </el-form-item>
      <el-form-item label="邮箱">
        <el-input v-model="searchForm.email" placeholder="请输入邮箱" clearable />
      </el-form-item>
      <el-form-item label="部门">
        <el-select v-model="searchForm.department_id" placeholder="请选择部门" clearable>
          <el-option 
            v-for="dept in departmentList" 
            :key="dept.id" 
            :label="dept.department_name" 
            :value="dept.id" 
          />
        </el-select>
      </el-form-item>
      <el-form-item label="状态">
        <el-select v-model="searchForm.status" placeholder="请选择状态" clearable>
          <el-option label="禁用" :value="0" />
          <el-option label="启用" :value="1" />
        </el-select>
      </el-form-item>
      <el-form-item>
        <el-button type="primary" @click="handleSearch">搜索</el-button>
        <el-button @click="handleReset">重置</el-button>
      </el-form-item>
    </el-form>

    <!-- 数据表格 -->
    <el-table v-loading="loading" :data="userList" border style="width: 100%">
      <el-table-column prop="id" label="ID" width="80" />
      <el-table-column prop="username" label="用户名" />
      <el-table-column prop="email" label="邮箱" />
      <el-table-column prop="phone" label="手机号" />
      <el-table-column prop="department_name" label="部门" />
      <el-table-column prop="status" label="状态" width="100">
        <template slot-scope="scope">
          <el-tag :type="scope.row.status === 1 ? 'success' : 'danger'">
            {{ scope.row.status === 1 ? '启用' : '禁用' }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column prop="create_time" label="创建时间" width="180" />
      <el-table-column label="操作" width="320" fixed="right">
        <template slot-scope="scope">
          <div class="table-operations">
            <el-button size="mini" type="text" @click="viewUser(scope.row)">查看</el-button>
            <el-button size="mini" type="text" class="edit-btn" @click="editUser(scope.row)">编辑</el-button>
            <el-button size="mini" type="text" class="warning-btn" @click="resetPassword(scope.row)">重置密码</el-button>
            <el-button size="mini" type="text" class="info-btn" @click="assignRoles(scope.row)">分配角色</el-button>
            <el-button size="mini" type="text" class="delete-btn" @click="deleteUser(scope.row)">删除</el-button>
          </div>
        </template>
      </el-table-column>
    </el-table>

    <!-- 分页 -->
    <div class="pagination">
      <el-pagination
        @size-change="handleSizeChange"
        @current-change="handleCurrentChange"
        :current-page="pagination.page"
        :page-sizes="[10, 20, 50, 100]"
        :page-size="pagination.limit"
        layout="total, sizes, prev, pager, next, jumper"
        :total="pagination.total"
      />
    </div>

    <!-- 用户表单对话框 -->
    <el-dialog :title="dialogTitle" :visible.sync="dialogVisible" width="600px">
      <el-form ref="userForm" :model="userForm" :rules="userRules" label-width="100px">
        <el-form-item label="用户名" prop="username">
          <el-input v-model="userForm.username" :disabled="isEdit" />
        </el-form-item>
        <el-form-item label="密码" prop="password" v-if="!isEdit">
          <el-input v-model="userForm.password" type="password" />
        </el-form-item>
        <el-form-item label="邮箱" prop="email">
          <el-input v-model="userForm.email" />
        </el-form-item>
        <el-form-item label="手机号" prop="phone">
          <el-input v-model="userForm.phone" />
        </el-form-item>
        <el-form-item label="部门" prop="department_id">
          <el-select v-model="userForm.department_id" placeholder="请选择部门">
            <el-option 
              v-for="dept in departmentList" 
              :key="dept.id" 
              :label="dept.department_name" 
              :value="dept.id" 
            />
          </el-select>
        </el-form-item>
        <el-form-item label="状态" prop="status">
          <el-radio-group v-model="userForm.status">
            <el-radio :label="1">启用</el-radio>
            <el-radio :label="0">禁用</el-radio>
          </el-radio-group>
        </el-form-item>
      </el-form>
      <div slot="footer" class="dialog-footer">
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" @click="saveUser">确定</el-button>
      </div>
    </el-dialog>

    <!-- 分配角色对话框 -->
    <el-dialog title="分配角色" :visible.sync="roleDialogVisible" width="500px">
      <el-form label-width="100px">
        <el-form-item label="用户名">
          <span>{{ currentUser.username }}</span>
        </el-form-item>
        <el-form-item label="角色">
          <el-checkbox-group v-model="selectedRoles">
            <el-checkbox 
              v-for="role in roleList" 
              :key="role.id" 
              :label="role.id"
            >
              {{ role.role_name }}
            </el-checkbox>
          </el-checkbox-group>
        </el-form-item>
      </el-form>
      <div slot="footer" class="dialog-footer">
        <el-button @click="roleDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="saveRoles">确定</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { getUserList, createUser, updateUser, deleteUser, resetUserPassword, assignUserRoles, getUserRoles } from '../../api/system'

export default {
  name: 'UserManagement',
  data() {
    return {
      loading: false,
      userList: [],
      departmentList: [],
      roleList: [],
      searchForm: {
        username: '',
        email: '',
        department_id: '',
        status: ''
      },
      pagination: {
        page: 1,
        limit: 20,
        total: 0
      },
      dialogVisible: false,
      dialogTitle: '新增用户',
      isEdit: false,
      userForm: {
        id: null,
        username: '',
        password: '',
        email: '',
        phone: '',
        department_id: '',
        status: 1
      },
      userRules: {
        username: [
          { required: true, message: '请输入用户名', trigger: 'blur' }
        ],
        password: [
          { required: true, message: '请输入密码', trigger: 'blur' },
          { min: 6, message: '密码长度不能少于6位', trigger: 'blur' }
        ],
        email: [
          { required: true, message: '请输入邮箱', trigger: 'blur' },
          { type: 'email', message: '请输入正确的邮箱格式', trigger: 'blur' }
        ]
      },
      roleDialogVisible: false,
      currentUser: {},
      selectedRoles: []
    }
  },
  created() {
    this.fetchUserList()
    this.fetchDepartmentList()
    this.fetchRoleList()
  },
  methods: {
    async fetchUserList() {
      this.loading = true
      try {
        const params = {
          ...this.searchForm,
          page: this.pagination.page,
          limit: this.pagination.limit
        }
        const response = await getUserList(params)
        if (response.code === 200) {
          this.userList = response.data.list
          this.pagination.total = response.data.total
        }
      } catch (error) {
        this.$message.error('获取用户列表失败')
      } finally {
        this.loading = false
      }
    },
    async fetchDepartmentList() {
      try {
        // 这里需要调用部门列表API
        // const response = await getDepartmentList()
        // this.departmentList = response.data
        this.departmentList = [
          { id: 1, department_name: '技术部' },
          { id: 2, department_name: '市场部' },
          { id: 3, department_name: '运营部' }
        ]
      } catch (error) {
        console.error('获取部门列表失败', error)
      }
    },
    async fetchRoleList() {
      try {
        // 这里需要调用角色列表API
        // const response = await getRoleList()
        // this.roleList = response.data
        this.roleList = [
          { id: 1, role_name: '超级管理员' },
          { id: 2, role_name: '管理员' },
          { id: 3, role_name: '普通用户' }
        ]
      } catch (error) {
        console.error('获取角色列表失败', error)
      }
    },
    handleSearch() {
      this.pagination.page = 1
      this.fetchUserList()
    },
    handleReset() {
      this.searchForm = {
        username: '',
        email: '',
        department_id: '',
        status: ''
      }
      this.pagination.page = 1
      this.fetchUserList()
    },
    handleSizeChange(val) {
      this.pagination.limit = val
      this.fetchUserList()
    },
    handleCurrentChange(val) {
      this.pagination.page = val
      this.fetchUserList()
    },
    showCreateDialog() {
      this.dialogTitle = '新增用户'
      this.isEdit = false
      this.userForm = {
        id: null,
        username: '',
        password: '',
        email: '',
        phone: '',
        department_id: '',
        status: 1
      }
      this.dialogVisible = true
    },
    viewUser(row) {
      // 查看用户详情
      this.$message.info('查看功能待实现')
    },
    editUser(row) {
      this.dialogTitle = '编辑用户'
      this.isEdit = true
      this.userForm = { ...row }
      this.dialogVisible = true
    },
    async saveUser() {
      try {
        await this.$refs.userForm.validate()
        let response
        if (this.isEdit) {
          response = await updateUser(this.userForm)
        } else {
          response = await createUser(this.userForm)
        }
        
        if (response.code === 200) {
          this.$message.success(this.isEdit ? '更新成功' : '创建成功')
          this.dialogVisible = false
          this.fetchUserList()
        } else {
          this.$message.error(response.msg || '操作失败')
        }
      } catch (error) {
        console.error('保存用户失败', error)
      }
    },
    async resetPassword(row) {
      try {
        const response = await resetUserPassword({ id: row.id })
        if (response.code === 200) {
          this.$message.success('重置密码成功，新密码：' + response.data.new_password)
        } else {
          this.$message.error(response.msg || '重置密码失败')
        }
      } catch (error) {
        this.$message.error('重置密码失败')
      }
    },
    async assignRoles(row) {
      this.currentUser = row
      try {
        const response = await getUserRoles({ user_id: row.id })
        if (response.code === 200) {
          this.selectedRoles = response.data.map(role => role.id)
        }
      } catch (error) {
        console.error('获取用户角色失败', error)
      }
      this.roleDialogVisible = true
    },
    async saveRoles() {
      try {
        const response = await assignUserRoles({
          user_id: this.currentUser.id,
          role_ids: this.selectedRoles
        })
        if (response.code === 200) {
          this.$message.success('分配角色成功')
          this.roleDialogVisible = false
        } else {
          this.$message.error(response.msg || '分配角色失败')
        }
      } catch (error) {
        this.$message.error('分配角色失败')
      }
    },
    async deleteUser(row) {
      try {
        await this.$confirm('确定要删除该用户吗？', '提示', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning'
        })
        
        const response = await deleteUser({ id: row.id })
        if (response.code === 200) {
          this.$message.success('删除成功')
          this.fetchUserList()
        } else {
          this.$message.error(response.msg || '删除失败')
        }
      } catch (error) {
        if (error !== 'cancel') {
          this.$message.error('删除失败')
        }
      }
    }
  }
}
</script>

<style scoped>
.user-management {
  padding: 20px;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.search-form {
  background: #f5f5f5;
  padding: 20px;
  border-radius: 4px;
  margin-bottom: 20px;
}

.pagination {
  margin-top: 20px;
  text-align: right;
}

.table-operations {
  display: flex;
  gap: 4px;
  justify-content: center;
  flex-wrap: nowrap;
}

.table-operations .el-button--text {
  padding: 2px 6px;
  font-size: 12px;
  border: none;
  background: none;
  white-space: nowrap;
  min-width: unset;
}

.table-operations .edit-btn {
  color: #409eff;
}

.table-operations .edit-btn:hover {
  color: #66b1ff;
  background-color: #ecf5ff;
}

.table-operations .warning-btn {
  color: #e6a23c;
}

.table-operations .warning-btn:hover {
  color: #ebb563;
  background-color: #fdf6ec;
}

.table-operations .info-btn {
  color: #909399;
}

.table-operations .info-btn:hover {
  color: #a6a9ad;
  background-color: #f4f4f5;
}

.table-operations .delete-btn {
  color: #f56c6c;
}

.table-operations .delete-btn:hover {
  color: #f78989;
  background-color: #fef0f0;
}
</style>